import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Smile } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import styles from './MessagingInbox.module.css';

const emojis = [
  ['😊', 'smile'],
  ['👋', 'wave'],
  ['🎉', 'celebration'],
  ['🙌', 'raised_hands'],
  ['✨', 'sparkles'],
  ['📅', 'calendar'],
  ['💼', 'briefcase'],
  ['✅', 'check'],
] as const;

/** Small, local picker using the same emoji set as the recruiting composer. */
export const EmojiPicker = ({
  textareaRef,
  value,
  onChange,
  disabled,
}: {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) => {
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const [open, setOpen] = useState(false);
  const selection = useRef({ start: value.length, end: value.length });
  const [range, setRange] = useState(selection.current);
  const restoreRange = useRef(selection.current);
  const returnToInput = useRef(false);
  useEffect(() => {
    const input = textareaRef.current;
    if (!input) return;
    const remember = () => {
      selection.current = {
        start: input.selectionStart,
        end: input.selectionEnd,
      };
    };
    const events = ['select', 'blur', 'keyup', 'pointerup', 'input'] as const;
    events.forEach((event) => input.addEventListener(event, remember));
    return () =>
      events.forEach((event) => input.removeEventListener(event, remember));
  }, [textareaRef]);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const restoreInputFocus = useCallback(() => {
    const input = textareaRef.current;
    if (!input || input.disabled) return;
    input.focus();
    input.setSelectionRange(
      restoreRange.current.start,
      restoreRange.current.end
    );
    selection.current = restoreRange.current;
  }, [textareaRef]);
  useLayoutEffect(() => {
    if (!open && returnToInput.current) restoreInputFocus();
  }, [open, value, restoreInputFocus]);
  const start = Math.min(range.start, value.length);
  const end = Math.min(Math.max(start, range.end), value.length);
  const remaining = 4000 - value.length + end - start;
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next && disabled) return;
        setRange(selection.current);
        if (next) returnToInput.current = false;
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={styles.emojiTrigger}
          disabled={disabled}
          aria-label={t('messaging.add_emoji')}
        >
          <Smile aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={styles.emojiPicker}
        align="start"
        side="top"
        aria-label={t('messaging.choose_emoji')}
        onEscapeKeyDown={() => {
          restoreRange.current = { start, end };
          returnToInput.current = true;
        }}
        onCloseAutoFocus={(event) => {
          if (!returnToInput.current) return;
          event.preventDefault();
          restoreInputFocus();
          returnToInput.current = false;
        }}
      >
        {emojis.map(([emoji, name]) => (
          <button
            key={name}
            type="button"
            aria-label={t(`messaging.emoji_names.${name}`)}
            disabled={disabled || emoji.length > remaining}
            onClick={() => {
              if (disabled || emoji.length > remaining) return;
              onChange(`${value.slice(0, start)}${emoji}${value.slice(end)}`);
              restoreRange.current = {
                start: start + emoji.length,
                end: start + emoji.length,
              };
              returnToInput.current = true;
              setOpen(false);
            }}
          >
            {emoji}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
